import { useGSAP } from '@gsap/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';

const useSectionAnimation = ({
  backgroundRef,
  titleRef,
  descriptionRef,
  buttonRef,
  imageUrl,
  isLoading = false,
}) => {
  const animationTimeline = useRef(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // The full-size image loads in the background; content reveal doesn't
  // wait on it (canReveal below only depends on isLoading). Until it's
  // ready, no background-image is set at all, so the section's own
  // background-color fallback shows through instead of a placeholder.
  useEffect(() => {
    if (!imageUrl) {
      setIsImageLoaded(false);
      return;
    }

    setIsImageLoaded(false);
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setIsImageLoaded(true);
    };
    img.onerror = () => {
      if (!cancelled) setIsImageLoaded(true);
    };
    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const backgroundImageUrl = isImageLoaded ? imageUrl : null;
  const canReveal = !isLoading;

  useLayoutEffect(() => {
    if (canReveal) {
      if (animationTimeline.current) {
        animationTimeline.current.play();
      }
    } else {
      if (animationTimeline.current) {
        animationTimeline.current.pause();
      }
    }
  }, [canReveal]);

  useGSAP(
    () => {
      if (!backgroundRef.current) {
        return;
      }

      let buttonHeight = 0;
      if (buttonRef.current) {
        gsap.set(buttonRef.current, {
          display: 'block',
          height: 'auto',
          visibility: 'hidden',
        });
        buttonHeight = buttonRef.current.offsetHeight;
      }

      if (titleRef.current) {
        gsap.set(titleRef.current, {
          opacity: 0,
          y: 5,
        });
      }

      if (descriptionRef.current) {
        gsap.set(descriptionRef.current, {
          height: 'auto',
          opacity: 1,
          y: 0,
          overflow: 'visible',
          display: 'block',
        });

        gsap.set(descriptionRef.current, {
          opacity: 0,
          y: 5,
          clipPath: 'inset(0 0 100% 0)',
          overflow: 'hidden',
          display: 'block',
        });
      }

      if (buttonRef.current) {
        gsap.set(buttonRef.current, {
          opacity: 0,
          y: buttonHeight,
          visibility: 'visible',
          display: 'block',
          height: 'auto',
        });
      }

      const tl = gsap.timeline({
        paused: true,
      });

      animationTimeline.current = tl;

      tl.fromTo(
        backgroundRef.current,
        { scale: 1.2 },
        {
          scale: 1,
          duration: 1.8,
          ease: 'power2.out',
          onComplete: () => {
            gsap.set(document.body, { overflowX: 'auto' });
            if (backgroundRef.current) {
              backgroundRef.current.style.transform = '';
            }
          },
        },
        0,
      );

      const titleAndDescriptionStartTime = 0.5;
      const contentAnimationDuration = 1.2;
      const contentEase = 'power2.out';

      if (titleRef.current) {
        tl.to(
          titleRef.current,
          {
            opacity: 1,
            y: 0,
            duration: contentAnimationDuration,
            ease: contentEase,
          },
          titleAndDescriptionStartTime,
        );
      }

      if (descriptionRef.current) {
        tl.to(
          descriptionRef.current,
          {
            opacity: 1,
            y: 0,
            clipPath: 'inset(0% 0 0% 0)',
            duration: contentAnimationDuration,
            ease: contentEase,
          },
          titleAndDescriptionStartTime,
        );
      }

      if (buttonRef.current) {
        const buttonTweenStartTime =
          titleAndDescriptionStartTime + contentAnimationDuration;

        tl.to(
          buttonRef.current,
          {
            opacity: 1,
            y: 0,
            duration: contentAnimationDuration,
            ease: contentEase,
          },
          buttonTweenStartTime,
        );
      }

      if (canReveal) {
        tl.play();
      }
    },
    {
      scope: backgroundRef,
      dependencies: [
        canReveal,
        backgroundRef,
        titleRef,
        descriptionRef,
        buttonRef,
      ],
    },
  );

  return { canReveal, backgroundImageUrl, isImageLoaded };
};

export default useSectionAnimation;
